import { useEffect, useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import api from '../lib/axios';

interface FeatureInfo {
  available: boolean;
  required_plan: string;
}

interface FeatureData {
  plan: string;
  features: Record<string, FeatureInfo>;
  limits: Record<string, number | string>;
}

let cachedData: FeatureData | null = null;

export function useFeatureFlags() {
  const { isAuthenticated } = useAuthStore();
  const [data, setData] = useState<FeatureData | null>(cachedData);
  const [loading, setLoading] = useState(!cachedData);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (cachedData) return;

    const fetch = async () => {
      try {
        const response = await api.get('/api/features/me');
        cachedData = response.data;
        setData(response.data);
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [isAuthenticated]);

  const hasFeature = (feature: string): boolean => {
    if (!data) return false;
    return data.features[feature]?.available ?? false;
  };

  const getLimit = (key: string): number | string => {
    if (!data) return 0;
    return data.limits[key] ?? 0;
  };

  const requiredPlan = (feature: string): string => {
    if (!data) return 'starter';
    return data.features[feature]?.required_plan ?? 'starter';
  };

  const invalidateCache = () => {
    cachedData = null;
  };

  return { data, loading, hasFeature, getLimit, requiredPlan, invalidateCache };
}
